import { errorHandler, successHandler } from "@/utils/functions";
import queryGenerator from "@/utils/queryGenarator";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const initialState = {
    list: null,
    university: null,
    totalCount: 0,
    error: "",
    loading: false,
    locations: [],
};

export const loadUniqueLocations = createAsyncThunk(
    "university/loadUniqueLocations",
    async () => {
        try {
            const { data } = await axios.get(`university/locations`);
            return successHandler(data);
        } catch (error) {
            return errorHandler(error);
        }
    }
);

export const loadAllUniversity = createAsyncThunk(
    "university/loadAllUniversity",
    async (arg = {}) => {
        try {
            let query = queryGenerator(arg);
            const { data } = await axios.get(`university?${query}`);
            return successHandler(data);
        } catch (error) {
            return errorHandler(error);
        }
    }
);

export const loadSingleUniversity = createAsyncThunk(
    "university/loadSingleUniversity",
    async (id) => {
        try {
            const { data } = await axios.get(`university/${id}`);
            return successHandler(data);
        } catch (error) {
            return errorHandler(error);
        }
    }
);

export const createUniversity = createAsyncThunk(
    "university/createUniversity",
    async (values) => {
        try {
            const { data } = await axios({
                method: "post",
                headers: {
                    Accept: "application/json",
                },
                url: `university`,
                data: values,
            });

            return successHandler(data, "University Created Successfully");
        } catch (error) {
            return errorHandler(error, true);
        }
    }
);

export const updateUniversity = createAsyncThunk(
    "university/updateUniversity",
    async ({ id, values }) => {
        try {
            const { data } = await axios({
                method: "put",
                headers: {
                    Accept: "application/json",
                },
                url: `university/${id}`,
                data: values,
            });
            return successHandler(data, "University Updated Successfully");
        } catch (error) {
            return errorHandler(error);
        }
    }
);

export const deleteUniversity = createAsyncThunk(
    "university/deleteUniversity",
    async ({ id }) => {
        try {
            const { data } = await axios({
                method: "delete",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                },
                url: `university/${id}`,
            });

            return successHandler(data, "University deleted successfully");
        } catch (error) {
            return errorHandler(error, true);
        }
    }
);

export const addMajorToUniversity = createAsyncThunk(
    "university/addMajorToUniversity",
    async ({ id, majorIds }) => {
        try {
            const { data } = await axios({
                method: "post",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                },
                url: `university/${id}/majors`,
                data: { majorIds },
            });

            return successHandler(data, "Majors added successfully");
        } catch (error) {
            return errorHandler(error, true);
        }
    }
);

export const removeMajorFromUniversity = createAsyncThunk(
    "university/removeMajorFromUniversity",
    async ({ id, majorId }) => {
        try {
            const { data } = await axios({
                method: "delete",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                },
                url: `university/${id}/majors/${majorId}`,
            });

            return successHandler(data, "Major removed successfully");
        } catch (error) {
            return errorHandler(error, true);
        }
    }
);

export const deleteUniversityAttachment = createAsyncThunk(
    "university/deleteUniversityAttachment",
    async ({ id, attachmentId }) => {
        try {
            const { data } = await axios({
                method: "delete",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                },
                url: `university/${id}/attachments/${attachmentId}`,
            });

            return successHandler(data, "Attachment removed successfully");
        } catch (error) {
            return errorHandler(error, true);
        }
    }
);

export const updateMajorPricingInUniversity = createAsyncThunk(
    "university/updateMajorPricingInUniversity",
    async ({ universityId, majorId, values }) => {
        try {
            const { data } = await axios({
                method: "put",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                },
                url: `university/${universityId}/majors/${majorId}/pricing`,
                data: values,
            });

            return successHandler(data, "Major pricing updated successfully");
        } catch (error) {
            return errorHandler(error, true);
        }
    }
);

const universitySlice = createSlice({
    name: "university",
    initialState,
    reducers: {
        clearUniversity: (state) => {
            state.university = null;
        },
        clearUniversityList: (state) => {
            state.list = [];
        },
    },
    extraReducers: (builder) => {
        // loadAllUniversity
        builder.addCase(loadAllUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(loadAllUniversity.fulfilled, (state, action) => {
            state.loading = false;
            const data = action.payload?.data;
            // Handle both response shapes: { universities, totalCount } and { getAllUniversity, totalUniversity }
            state.list = data?.universities ?? data?.getAllUniversity ?? data;
            state.totalCount = data?.totalCount ?? data?.totalUniversity ?? 0;
        });
        builder.addCase(loadAllUniversity.rejected, (state) => {
            state.loading = false;
        });

        // loadUniqueLocations
        builder.addCase(loadUniqueLocations.fulfilled, (state, action) => {
            state.locations = action.payload?.data || [];
        });

        // loadSingleUniversity
        builder.addCase(loadSingleUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(loadSingleUniversity.fulfilled, (state, action) => {
            state.loading = false;
            const data = action.payload?.data;
            state.university = data?.university ?? data;
        });
        builder.addCase(loadSingleUniversity.rejected, (state) => {
            state.loading = false;
        });

        // createUniversity
        builder.addCase(createUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(createUniversity.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(createUniversity.rejected, (state) => {
            state.loading = false;
        });

        // updateUniversity
        builder.addCase(updateUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateUniversity.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(updateUniversity.rejected, (state) => {
            state.loading = false;
        });

        // deleteUniversity
        builder.addCase(deleteUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteUniversity.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteUniversity.rejected, (state) => {
            state.loading = false;
        });

        // addMajorToUniversity
        builder.addCase(addMajorToUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(addMajorToUniversity.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data?.university) {
                state.university = action.payload.data.university;
            }
        });
        builder.addCase(addMajorToUniversity.rejected, (state) => {
            state.loading = false;
        });

        // removeMajorFromUniversity
        builder.addCase(removeMajorFromUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(removeMajorFromUniversity.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(removeMajorFromUniversity.rejected, (state) => {
            state.loading = false;
        });
        // deleteUniversityAttachment
        builder.addCase(deleteUniversityAttachment.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteUniversityAttachment.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteUniversityAttachment.rejected, (state) => {
            state.loading = false;
        });

        // updateMajorPricingInUniversity
        builder.addCase(updateMajorPricingInUniversity.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateMajorPricingInUniversity.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(updateMajorPricingInUniversity.rejected, (state) => {
            state.loading = false;
        });
    },
});

export default universitySlice.reducer;
export const { clearUniversity, clearUniversityList } = universitySlice.actions;
